import type { Feature } from "geojson";

import { isShapeGeometryType } from "@/features/map/lib/mapbox-geometry-filters";
import { MAP_CLUSTER_POINT_COUNT_MIN } from "@/utils/map/map-cluster-filters";

export interface MapTapCoordinates {
	longitude: number;
	latitude: number;
}

/**
 * Mapbox cluster features use `point_count` ≥ 2 for multi-point clusters.
 * Unclustered points may still carry `point_count: 1` from the engine.
 */
export function isClusterMapFeature(
	props: Record<string, unknown> | null | undefined,
): boolean {
	if (props == null) return false;
	const raw = props.point_count;
	if (raw == null) return false;
	const n = typeof raw === "number" ? raw : Number(raw);
	if (!Number.isFinite(n)) return false;
	return n >= MAP_CLUSTER_POINT_COUNT_MIN;
}

function squaredDistanceToTap(
	feature: Feature,
	tap: MapTapCoordinates,
): number | null {
	if (feature.geometry?.type !== "Point") return null;
	const coordinates = feature.geometry.coordinates;
	if (coordinates.length < 2) return null;
	const dLng = coordinates[0] - tap.longitude;
	const dLat = coordinates[1] - tap.latitude;
	return dLng * dLng + dLat * dLat;
}

/**
 * Chooses the map feature that best matches a click hit list:
 * prefer line/polygon hits (so points don't steal shape clicks), then
 * unclustered markers, then the point nearest the tap.
 */
export function pickPrimaryMapFeature(
	features: Feature[] | undefined,
	tapCoordinates: MapTapCoordinates,
): Feature | undefined {
	if (features == null || features.length === 0) return undefined;
	if (features.length === 1) return features[0];

	const nonCluster = features.filter((feature) => {
		const props = feature.properties as Record<string, unknown> | undefined;
		return !isClusterMapFeature(props);
	});

	const candidates = nonCluster.length > 0 ? nonCluster : features;

	// Topmost shape win — queryRenderedFeatures is ordered top→bottom.
	const shapeHit = candidates.find((feature) =>
		isShapeGeometryType(feature.geometry?.type),
	);
	if (shapeHit != null) return shapeHit;

	let closest = candidates[0];
	let closestDistance = squaredDistanceToTap(closest, tapCoordinates);

	for (let index = 1; index < candidates.length; index += 1) {
		const candidate = candidates[index];
		const distance = squaredDistanceToTap(candidate, tapCoordinates);
		if (distance == null) continue;
		if (closestDistance == null || distance < closestDistance) {
			closest = candidate;
			closestDistance = distance;
		}
	}

	return closest;
}
